/**
 * Marken (Multi-Brand / White-Label, N3-5) — operator-only Verwaltung.
 *
 * Eine Marke liefert Theming-Defaults (Anzeigename, Primärfarbe, Logo,
 * Support-Adresse) plus die White-Label-Extras (Produktname, Kurzname,
 * Claim, Akzentfarbe, Dark-Logo, Favicon, Support-/Doku-/Rechts-Links,
 * Mail-Absender), die neue Mandanten unter dieser Marke erben. Diese Seite
 * listet alle Marken und erlaubt Anlegen, Bearbeiten und Archivieren/Löschen.
 * Die Haus-Marke `sectepe` ist serverseitig gegen Löschen geschützt — ein
 * solcher Versuch landet als Fehler-Banner. Der Deployment-Default
 * (`BRAND_SLUG`, aus GET /api/brands-meta) ist die Marke, die
 * `/api/public/brand` ohne Tenant ausliefert — er wird in der Tabelle
 * markiert.
 *
 * Muster: Tabelle + Inline-Upsert-Form (vier Abschnitte, Farb-Picker analog
 * BrandingSection) + Live-Vorschau + axios CRUD. Backend: GET/POST
 * /api/brands, PATCH/DELETE /api/brands/:slug, GET /api/brands-meta.
 * Validierung clientseitig zur Sofort-Rückmeldung; der Server bleibt
 * autoritativ (gleiche Regeln: https-only, #rrggbb, Längen).
 */
import { useEffect, useMemo, useState, FormEvent } from 'react';
import api, {
  BrandView,
  BrandsMeta,
  BrandCreateInput,
  BrandUpdateInput,
  BRAND_EXTRA_KEYS,
  createBrand,
  extractApiError,
  getBrandsMeta,
  updateBrand,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { labelForBrandStatus } from '../services/status-labels';
import {
  RAMP_STEPS,
  contrastText,
  deriveColorRamp,
  emailFromName,
  isEmailAddress,
  isHexColor,
  isHttpsUrl,
} from '../services/brand';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-slate-200 text-slate-600',
};

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;
const DEFAULT_PRIMARY_COLOR = '#1a64ed';

// Längen-Caps wie brand.service.ts (SHORT_NAME_MAX_LEN etc.).
const DISPLAY_NAME_MAX = 120;
const SHORT_NAME_MAX = 12;
const TAGLINE_MAX = 160;
const EMAIL_FOOTER_MAX = 300;

export function BrandsPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<BrandView[]>([]);
  const [meta, setMeta] = useState<BrandsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BrandView | 'new' | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh(): void {
    setLoading(true);
    api.get<BrandView[]>('/brands')
      .then((r) => setRows(r.data))
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, []);

  // Meta ist rein informativ (Badge + Hinweis) — ein Fehler hier blockiert
  // die Seite nicht und produziert kein Banner.
  useEffect(() => {
    let cancelled = false;
    getBrandsMeta().then((m) => { if (!cancelled) setMeta(m); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function toggleArchive(b: BrandView): Promise<void> {
    const next = b.status === 'active' ? 'archived' : 'active';
    setBusySlug(b.slug);
    setError(null);
    try {
      await api.patch(`/brands/${b.slug}`, { status: next });
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusySlug(null);
    }
  }

  async function remove(b: BrandView): Promise<void> {
    if (!window.confirm(`Marke "${b.displayName}" (${b.slug}) löschen?`)) return;
    setBusySlug(b.slug);
    setError(null);
    try {
      await api.delete(`/brands/${b.slug}`);
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusySlug(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Marken</h2>
          <p className="text-sm text-slate-500">
            White-Label-Theming-Defaults. Mandanten, die unter einer Marke
            provisioniert werden, erben deren Werte (überschreibbar je Mandant).
          </p>
          {meta && (
            <p className="text-xs text-slate-500 mt-1">
              Deployment-Default: <span className="font-mono">{meta.defaultBrandSlug}</span> — diese
              Marke liefert <span className="font-mono">/api/public/brand</span> (Login-Seiten,
              Operator-Konsole, Mails ohne Tenant-Bezug). Setzen per{' '}
              <span className="font-mono">BRAND_SLUG</span>.
            </p>
          )}
        </div>
        <button
          onClick={() => setEditing((e) => (e === null ? 'new' : null))}
          className="btn-primary"
          disabled={isReadOnly}
        >
          {editing ? 'Schliessen' : 'Marke anlegen'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {editing && !isReadOnly && (
        <UpsertForm
          brand={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
          onError={setError}
        />
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3">Slug</th>
              <th className="p-3">Anzeigename</th>
              <th className="p-3">Farbe</th>
              <th className="p-3">Support</th>
              <th className="p-3">Status</th>
              <th className="p-3">Aktionen</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((b) => (
              <tr key={b.slug} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono">
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    {b.slug}
                    {meta?.defaultBrandSlug === b.slug && (
                      <span
                        className="badge bg-blue-100 text-blue-800 font-sans"
                        title="Diese Marke liefert /api/public/brand (BRAND_SLUG)."
                      >
                        Deployment-Default
                      </span>
                    )}
                    {meta?.houseBrandSlug === b.slug && (
                      <span
                        className="badge bg-slate-100 text-slate-600 font-sans"
                        title="Haus-Marke — serverseitig gegen Löschen geschützt."
                      >
                        Haus-Marke
                      </span>
                    )}
                  </span>
                </td>
                <td className="p-3">
                  <div className="font-medium">{b.displayName}</div>
                  {b.productName && (
                    <div className="text-xs text-slate-500">{b.productName}</div>
                  )}
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block h-4 w-4 rounded border"
                      style={{ backgroundColor: b.primaryColor }}
                    />
                    <span className="font-mono text-slate-600">{b.primaryColor}</span>
                  </span>
                </td>
                <td className="p-3 text-slate-600">{b.supportEmail ?? '—'}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[b.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {labelForBrandStatus(b.status)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setEditing(b)}
                      disabled={busySlug === b.slug || isReadOnly}
                      className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => toggleArchive(b)}
                      disabled={busySlug === b.slug || isReadOnly}
                      className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      {b.status === 'active' ? 'Archivieren' : 'Reaktivieren'}
                    </button>
                    <button
                      onClick={() => remove(b)}
                      disabled={busySlug === b.slug || isReadOnly}
                      className="text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">
                Noch keine Marken angelegt.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formular
// ---------------------------------------------------------------------------

/** Alle editierbaren Felder als Strings ('' = nicht gesetzt). */
type FormValues = {
  displayName: string;
  primaryColor: string;
  logoUrl: string;
  supportEmail: string;
} & Record<(typeof BRAND_EXTRA_KEYS)[number], string>;

type FieldKey = keyof FormValues;

interface FieldDef {
  key: FieldKey;
  label: string;
  placeholder?: string;
  kind: 'text' | 'url' | 'email' | 'textarea';
  maxLength?: number;
  mono?: boolean;
  hint?: string;
}

interface SectionDef {
  title: string;
  description: string;
  fields: FieldDef[];
}

// Primärfarbe und Akzentfarbe haben eigenes Markup (Color-Picker) und sind
// deshalb nicht Teil der generischen Feld-Listen.
const SECTIONS: SectionDef[] = [
  {
    title: 'Identität',
    description: 'Namen und Claim, wie sie in Titeln, App-Switcher und Login-Seiten erscheinen.',
    fields: [
      { key: 'displayName', label: 'Anzeigename', placeholder: 'z.B. Acme Cloud', kind: 'text', maxLength: DISPLAY_NAME_MAX },
      { key: 'productName', label: 'Produktname (optional)', placeholder: 'z.B. Acme Comm', kind: 'text', maxLength: DISPLAY_NAME_MAX, hint: 'Leer = Anzeigename bzw. Haus-Produktname.' },
      { key: 'shortName', label: 'Kurzname (optional, ≤ 12 Zeichen)', placeholder: 'z.B. Acme', kind: 'text', maxLength: SHORT_NAME_MAX },
      { key: 'tagline', label: 'Claim / Tagline (optional, ≤ 160 Zeichen)', placeholder: 'z.B. Sichere Kommunikation für Teams', kind: 'text', maxLength: TAGLINE_MAX },
      { key: 'websiteUrl', label: 'Website-URL (https, optional)', placeholder: 'https://www.acme.example', kind: 'url', mono: true },
    ],
  },
  {
    title: 'Farben & Logos',
    description: 'Primärfarbe = Ramp-Stufe 500; Akzentfarbe leer = Stufe 300 der Ramp.',
    fields: [
      { key: 'logoUrl', label: 'Logo-URL (https, optional)', placeholder: 'https://cdn.acme.example/logo.png', kind: 'url', mono: true },
      { key: 'logoUrlDark', label: 'Logo-URL für dunkle Hintergründe (https, optional)', placeholder: 'https://cdn.acme.example/logo-dark.png', kind: 'url', mono: true, hint: 'Leer = Logo-URL.' },
      { key: 'faviconUrl', label: 'Favicon-URL (https, optional)', placeholder: 'https://cdn.acme.example/favicon.png', kind: 'url', mono: true },
    ],
  },
  {
    title: 'Support & Recht',
    description: 'Links im Footer der Portale und in Mails; nur gesetzte Links werden angezeigt.',
    fields: [
      { key: 'supportEmail', label: 'Support-Email (optional)', placeholder: 'support@acme.example', kind: 'email' },
      { key: 'supportUrl', label: 'Support-URL (https, optional)', placeholder: 'https://support.acme.example', kind: 'url', mono: true },
      { key: 'docsUrl', label: 'Dokumentations-URL (https, optional)', placeholder: 'https://docs.acme.example', kind: 'url', mono: true },
      { key: 'imprintUrl', label: 'Impressum-URL (https, optional)', placeholder: 'https://www.acme.example/impressum', kind: 'url', mono: true },
      { key: 'privacyUrl', label: 'Datenschutz-URL (https, optional)', placeholder: 'https://www.acme.example/datenschutz', kind: 'url', mono: true },
      { key: 'termsUrl', label: 'AGB-URL (https, optional)', placeholder: 'https://www.acme.example/agb', kind: 'url', mono: true },
    ],
  },
  {
    title: 'E-Mail',
    description: 'Absendername und Fusszeile transaktionaler Benachrichtigungen.',
    fields: [
      { key: 'emailFromName', label: 'Absendername (optional)', placeholder: 'z.B. Acme Cloud Benachrichtigungen', kind: 'text', maxLength: DISPLAY_NAME_MAX, hint: 'Leer = "<Anzeigename> Benachrichtigungen".' },
      { key: 'emailFooter', label: 'Mail-Fusszeile (optional, ≤ 300 Zeichen)', placeholder: 'Acme Cloud GmbH · Musterstrasse 1 · 12345 Musterstadt', kind: 'textarea', maxLength: EMAIL_FOOTER_MAX },
    ],
  },
];

function initialValues(brand: BrandView | null): FormValues {
  const v = {
    displayName: brand?.displayName ?? '',
    primaryColor: brand?.primaryColor ?? DEFAULT_PRIMARY_COLOR,
    logoUrl: brand?.logoUrl ?? '',
    supportEmail: brand?.supportEmail ?? '',
  } as FormValues;
  for (const key of BRAND_EXTRA_KEYS) v[key] = brand?.[key] ?? '';
  return v;
}

function validateField(def: FieldDef, raw: string): string | null {
  const v = raw.trim();
  if (def.key === 'displayName') {
    return v.length > 0 && raw.length <= DISPLAY_NAME_MAX ? null : `1 bis ${DISPLAY_NAME_MAX} Zeichen erforderlich.`;
  }
  if (v === '') return null;
  if (def.kind === 'url' && !isHttpsUrl(v)) return 'Nur https://-URLs sind zulässig.';
  if (def.kind === 'email' && !isEmailAddress(v)) return 'Bitte eine gültige Email-Adresse eingeben.';
  if (def.maxLength && raw.length > def.maxLength) return `Maximal ${def.maxLength} Zeichen.`;
  return null;
}

/** Feld-Fehler für alle generischen Felder (ohne Farben). */
function validateAll(values: FormValues): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {};
  for (const section of SECTIONS) {
    for (const def of section.fields) {
      const e = validateField(def, values[def.key]);
      if (e) errors[def.key] = e;
    }
  }
  return errors;
}

interface UpsertFormProps {
  /** null = anlegen; sonst bearbeiten (Slug ist dann unveränderlich). */
  brand: BrandView | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function UpsertForm({ brand, onClose, onSaved, onError }: UpsertFormProps) {
  const isEdit = brand !== null;
  const [slug, setSlug] = useState(brand?.slug ?? '');
  const [values, setValues] = useState<FormValues>(() => initialValues(brand));
  const [busy, setBusy] = useState(false);

  const set = (key: FieldKey) => (value: string) => setValues((v) => ({ ...v, [key]: value }));

  const slugValid = isEdit || SLUG_RE.test(slug);
  const colorValid = isHexColor(values.primaryColor);
  const accentValid = values.accentColor.trim() === '' || isHexColor(values.accentColor.trim());
  const fieldErrors = useMemo(() => validateAll(values), [values]);
  const allValid = slugValid && colorValid && accentValid && Object.keys(fieldErrors).length === 0;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!allValid) {
      onError('Slug, Anzeigename und gültige Hex-Farbe erforderlich; URLs nur https.');
      return;
    }
    setBusy(true);
    try {
      const trimmed = (key: FieldKey) => values[key].trim();
      const color = (key: 'primaryColor' | 'accentColor') => trimmed(key).toLowerCase();
      if (isEdit) {
        // PATCH sendet — wie bisher — alle Felder; '' → null löscht den Wert.
        const body: BrandUpdateInput = {
          displayName: trimmed('displayName'),
          primaryColor: color('primaryColor'),
          logoUrl: trimmed('logoUrl') === '' ? null : trimmed('logoUrl'),
          supportEmail: trimmed('supportEmail') === '' ? null : trimmed('supportEmail'),
        };
        for (const key of BRAND_EXTRA_KEYS) {
          const v = key === 'accentColor' ? color(key) : trimmed(key);
          body[key] = v === '' ? null : v;
        }
        await updateBrand(brand.slug, body);
      } else {
        // POST lässt leere Felder weg (Server-Default = nicht gesetzt).
        const body: BrandCreateInput = {
          slug,
          displayName: trimmed('displayName'),
          primaryColor: color('primaryColor'),
          logoUrl: trimmed('logoUrl') === '' ? undefined : trimmed('logoUrl'),
          supportEmail: trimmed('supportEmail') === '' ? undefined : trimmed('supportEmail'),
        };
        for (const key of BRAND_EXTRA_KEYS) {
          const v = key === 'accentColor' ? color(key) : trimmed(key);
          if (v !== '') body[key] = v;
        }
        await createBrand(body);
      }
      onSaved();
    } catch (err) {
      onError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const renderField = (def: FieldDef) => {
    const err = fieldErrors[def.key];
    const cls = `w-full px-2 py-1 rounded border text-sm${def.mono ? ' font-mono' : ''}`;
    return (
      <div key={def.key}>
        <label className="block text-sm font-medium mb-1">{def.label}</label>
        {def.kind === 'textarea' ? (
          <textarea
            value={values[def.key]}
            onChange={(e) => set(def.key)(e.target.value)}
            placeholder={def.placeholder}
            maxLength={def.maxLength}
            rows={2}
            className={cls}
          />
        ) : (
          <input
            type="text"
            value={values[def.key]}
            onChange={(e) => set(def.key)(e.target.value)}
            placeholder={def.placeholder}
            maxLength={def.maxLength}
            className={cls}
            required={def.key === 'displayName'}
          />
        )}
        {err ? (
          <p className="text-xs text-red-600 mt-1">{err}</p>
        ) : def.hint ? (
          <p className="text-xs text-slate-400 mt-1">{def.hint}</p>
        ) : null}
      </div>
    );
  };

  const renderColorField = (key: 'primaryColor' | 'accentColor') => {
    const isPrimary = key === 'primaryColor';
    const value = values[key];
    const valid = isPrimary ? colorValid : accentValid;
    return (
      <div>
        <label className="block text-sm font-medium mb-1">
          {isPrimary ? 'Primärfarbe' : 'Akzentfarbe (optional)'}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={isHexColor(value) ? value : (isPrimary ? DEFAULT_PRIMARY_COLOR : deriveColorRamp(values.primaryColor)[300])}
            onChange={(e) => set(key)(e.target.value)}
            className="h-9 w-12 rounded border"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => set(key)(e.target.value)}
            placeholder={isPrimary ? '#1a64ed' : 'leer = Ramp-Stufe 300'}
            className="flex-1 px-2 py-1 rounded border font-mono text-sm"
          />
          {!isPrimary && value !== '' && (
            <button type="button" onClick={() => set(key)('')} className="text-xs text-slate-500 hover:underline">
              Zurücksetzen
            </button>
          )}
        </div>
        {!valid && (
          <p className="text-xs text-red-600 mt-1">
            Hex-Farbcode erwartet (z.B. <span className="font-mono">#1a64ed</span>).
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={submit} className="bg-white shadow rounded p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">{isEdit ? `Marke bearbeiten: ${brand.slug}` : 'Marke anlegen'}</h3>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">×</button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
        <div className="space-y-5">
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="z.B. partner-acme"
                className="w-full px-2 py-1 rounded border font-mono text-sm"
                required
              />
              {!slugValid && slug.length > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Kleinbuchstaben-Slug erwartet (a-z0-9-, 2–32 Zeichen).
                </p>
              )}
            </div>
          )}
          {SECTIONS.map((section) => (
            <fieldset key={section.title} className="border rounded p-3 space-y-3">
              <legend className="px-1 text-sm font-semibold text-slate-700">{section.title}</legend>
              <p className="text-xs text-slate-500 -mt-1">{section.description}</p>
              {section.title === 'Farben & Logos' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {renderColorField('primaryColor')}
                  {renderColorField('accentColor')}
                </div>
              )}
              {section.fields.map(renderField)}
            </fieldset>
          ))}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-3 py-1 rounded border" disabled={busy}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !allValid}>
              {busy ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </div>
        <BrandPreview values={values} />
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Live-Vorschau
// ---------------------------------------------------------------------------

const LEGAL_LINKS: Array<{ key: FieldKey; label: string }> = [
  { key: 'imprintUrl', label: 'Impressum' },
  { key: 'privacyUrl', label: 'Datenschutz' },
  { key: 'termsUrl', label: 'AGB' },
  { key: 'supportUrl', label: 'Support' },
  { key: 'docsUrl', label: 'Dokumentation' },
];

/**
 * Zeigt die Formularwerte (nicht die gespeicherten) so, wie Portale sie
 * rendern: Header in Primärfarbe mit Logo/Wortmarke, Farb-Ramp, Beispiel-
 * Button, Footer mit gesetzten Rechtslinks, Mail-Absender. Ungültige Farben
 * fallen auf den Default zurück, damit die Vorschau nie leer rendert.
 */
function BrandPreview({ values }: { values: FormValues }) {
  const primary = isHexColor(values.primaryColor) ? values.primaryColor.toLowerCase() : DEFAULT_PRIMARY_COLOR;
  const ramp = deriveColorRamp(primary);
  const accent = isHexColor(values.accentColor.trim()) ? values.accentColor.trim().toLowerCase() : ramp[300];
  const name = values.displayName.trim() || 'Marke';
  const productName = values.productName.trim() || name;
  const logo = isHttpsUrl(values.logoUrl.trim()) ? values.logoUrl.trim() : null;
  const onPrimary = contrastText(primary);
  const links = LEGAL_LINKS.filter((l) => isHttpsUrl(values[l.key].trim()));
  const supportEmail = isEmailAddress(values.supportEmail.trim()) ? values.supportEmail.trim() : null;
  const fromName = emailFromName({ name, emailFromName: values.emailFromName.trim() });

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-600">Vorschau</h4>
      <div className="rounded shadow overflow-hidden border">
        <div
          className="px-4 py-3 flex items-center justify-between gap-3"
          style={{ backgroundColor: primary, color: onPrimary }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {logo ? (
              <img src={logo} alt={name} className="h-6 w-auto" />
            ) : (
              <span className="font-brand text-base">{name}</span>
            )}
            <span className="text-xs opacity-80 truncate">{productName} Tenant Portal</span>
          </div>
          <span className="text-xs opacity-80 whitespace-nowrap">Plan · Logout</span>
        </div>
        <div className="p-4 space-y-3 bg-white">
          {values.tagline.trim() && (
            <p className="text-sm text-slate-600 italic">{values.tagline.trim()}</p>
          )}
          <div className="flex gap-1" aria-label="Farb-Ramp">
            {RAMP_STEPS.map((step) => (
              <span
                key={step}
                className="flex-1 h-8 rounded flex items-end justify-center text-[10px] font-mono"
                style={{ backgroundColor: ramp[step], color: contrastText(ramp[step]) }}
                title={`${step}: ${ramp[step]}`}
              >
                {step}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className="px-3 py-2 rounded font-medium text-sm"
              style={{ backgroundColor: primary, color: onPrimary }}
            >
              Primär-Aktion
            </button>
            <button
              type="button"
              className="px-3 py-2 rounded font-medium text-sm"
              style={{ backgroundColor: accent, color: contrastText(accent) }}
            >
              Akzent
            </button>
            <span className="text-xs font-mono text-slate-500">{primary} / {accent}</span>
          </div>
        </div>
        <div className="bg-slate-50 px-4 py-3 text-xs text-slate-500 border-t flex flex-wrap items-center justify-between gap-2">
          <span>{name}</span>
          <span className="flex flex-wrap gap-3">
            {links.map((l) => (
              <span key={l.key} className="underline" style={{ color: ramp[700] }}>{l.label}</span>
            ))}
            {supportEmail && <span className="text-slate-700">Support: {supportEmail}</span>}
            {links.length === 0 && !supportEmail && <span>— keine Links gesetzt —</span>}
          </span>
        </div>
      </div>
      <div className="rounded border bg-white p-3 text-xs text-slate-600 space-y-1">
        <div>
          <span className="text-slate-400">Mail-Absender:</span>{' '}
          <span className="font-medium">{fromName}</span>
          {supportEmail && <span className="font-mono text-slate-500"> &lt;{supportEmail}&gt;</span>}
        </div>
        {values.emailFooter.trim() && (
          <div className="text-slate-500 whitespace-pre-line">{values.emailFooter.trim()}</div>
        )}
      </div>
    </div>
  );
}
